import { useEffect, useState } from "react";
import type { ZodType } from "zod";

export default function usePersistantState<T>(
  key: string,
  schema: ZodType<T>,
  initialValue: T,
  storage: Storage = localStorage,
): [T, (value: T) => void] {
  const [state, setInternalState] = useState<T>(initialValue);

  useEffect(() => {
    const initState = getPersistantState<T>(key, storage);
    if (!initState) {
      storage.removeItem(key);
      return;
    }
    try {
      setInternalState(schema.parse(initState));
    } catch (error) {
      console.error(
        `Failed to parse persistant state, removing item with key ${key} from ${storage}`,
        error,
      );
      storage.removeItem(key);
    }
  }, [key, storage, schema]);

  const setState = (value: T) => {
    storage.setItem(key, JSON.stringify(value));
    setInternalState(value);
  };

  return [state, setState];
}

export const getPersistantState = <T>(
  key: string,
  storage: Storage,
): T | null => {
  const value = storage.getItem(key);
  if (!value) return null;
  return JSON.parse(value);
};

export const deletePersistantState = (key: string, storage: Storage) => {
  storage.removeItem(key);
};
